/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, Chip, CircularProgress, TextField, InputAdornment,
  Button, Typography, Table, TableBody, TableCell, TableRow, Alert,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import MDBox from 'components/MDBox';
import MDDatePicker from 'components/MDDatePicker';
import { api } from '../../api';
import { DROPDOWN_SX } from '../opdAppointments/constants';
import { useListUrlState } from '../../hooks/useListUrlState';

const toISO = (d) => d.toISOString().split('T')[0];
const defaultTo = toISO(new Date());
const defaultFrom = (() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return toISO(d); })();

const TEAL = '#00798E';
const TEAL_DARK = '#005f70';
const TEAL_LIGHT = '#e0f4f7';
// Same "fetch a capped window per source, merge client-side" approach as
// AccountsPaymentsPage — Pharmacy and OPD are genuinely separate
// microservices/tables (documents were never meant to be reunified on the
// backend), so this is a frontend-only merge. Revisit with real
// server-side cross-department pagination if a clinic's per-page volume
// ever exceeds this.
const FETCH_CAP = 300;
const PER_PAGE = 20;

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const DOC_TYPE_STYLE = {
  BILL:         { bg: '#e3f2fd', color: '#1565c0', label: 'Bill' },
  CREDIT_NOTE:  { bg: '#ede7f6', color: '#4527a0', label: 'Credit Note' },
  DEBIT_NOTE:   { bg: '#fce4ec', color: '#ad1457', label: 'Debit Note' },
};

const DEPT_STYLE = {
  PHARMACY: { bg: '#ede7f6', color: '#4527a0', icon: LocalPharmacyIcon },
  OPD:      { bg: '#e0f4f7', color: TEAL_DARK, icon: MedicalServicesIcon },
};

const STATUS_STYLE = {
  // Bills
  FINALIZED: { bg: '#e8f5e9', color: '#2e7d32' },
  PAID: { bg: '#e8f5e9', color: '#2e7d32' },
  DRAFT: { bg: '#fff3e0', color: '#e65100' },
  PENDING: { bg: '#fff3e0', color: '#e65100' },
  PARTIALLY_RETURNED: { bg: '#fff3e0', color: '#e65100' },
  PARTIALLY_REFUNDED: { bg: '#fff3e0', color: '#e65100' },
  CANCELLED: { bg: '#fdecea', color: '#c62828' },
  REFUNDED: { bg: '#fdecea', color: '#c62828' },
  // Credit/Debit notes
  OPEN: { bg: '#e3f2fd', color: '#1565c0' },
  USED: { bg: '#f5f5f5', color: '#757575' },
  APPLIED: { bg: '#f5f5f5', color: '#757575' },
  EXPIRED: { bg: '#fdecea', color: '#c62828' },
};

function StatCard({ icon, label, value, accent, sub }) {
  return (
    <Box sx={{
      flex: '1 1 160px', minWidth: 140, borderRadius: 3, background: '#fff',
      border: `1.5px solid ${accent}33`, boxShadow: `0 2px 12px ${accent}18`,
      px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.8,
    }}>
      <Box sx={{ width: 44, height: 44, borderRadius: 2.5, background: `linear-gradient(135deg, ${accent}22, ${accent}44)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {React.cloneElement(icon, { sx: { color: accent, fontSize: 22 } })}
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.2 }}>{label}</Typography>
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#1a202c', lineHeight: 1.4, fontFamily: 'monospace' }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: '0.65rem', color: '#bdbdbd', mt: 0.2 }}>{sub}</Typography>}
      </Box>
    </Box>
  );
}

function DocTypeBadge({ docType }) {
  const s = DOC_TYPE_STYLE[docType] || { bg: '#f5f5f5', color: '#757575', label: docType };
  return (
    <Chip label={s.label} size="small" sx={{ fontSize: '0.64rem', fontWeight: 700, height: 20, background: s.bg, color: s.color }} />
  );
}

function DeptBadge({ department }) {
  const s = DEPT_STYLE[department] || { bg: '#f5f5f5', color: '#757575', icon: DescriptionIcon };
  const Icon = s.icon;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3, borderRadius: '20px', background: s.bg }}>
      <Icon sx={{ fontSize: 12, color: s.color }} />
      <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: s.color, lineHeight: 1 }}>{department}</Typography>
    </Box>
  );
}

function StatusChip({ status }) {
  const s = STATUS_STYLE[status] || { bg: '#f5f5f5', color: '#757575' };
  return <Chip label={status} size="small" sx={{ fontSize: '0.62rem', fontWeight: 700, height: 20, background: s.bg, color: s.color }} />;
}

function opdContactName(contactInfo, contactId) {
  if (!contactInfo) return `Patient #${contactId}`;
  return [contactInfo.first_name, contactInfo.middle_name, contactInfo.last_name].filter(Boolean).join(' ') || `Patient #${contactId}`;
}

function normalizePharmacyBills(rows) {
  return (rows || []).map(b => ({
    key: `pharmacy-bill-${b.id}`,
    doc_type: 'BILL',
    department: 'PHARMACY',
    doc_no: b.bill_no,
    date: b.bill_date,
    party: b.patient_name || '—',
    amount: b.net_amount,
    status: b.status,
    route: `/pharmacy/billing/${b.id}`,
  }));
}

function normalizePharmacyCreditNotes(rows) {
  return (rows || []).map(cn => ({
    key: `pharmacy-cn-${cn.id}`,
    doc_type: 'CREDIT_NOTE',
    department: 'PHARMACY',
    doc_no: cn.credit_note_no,
    date: cn.credit_note_date,
    party: cn.original_bill?.patient_name || '—',
    amount: cn.amount,
    status: cn.status,
    route: cn.irm_pharm_bills_id ? `/pharmacy/billing/${cn.irm_pharm_bills_id}` : null,
  }));
}

function normalizePharmacyDebitNotes(rows) {
  return (rows || []).map(dn => ({
    key: `pharmacy-dn-${dn.id}`,
    doc_type: 'DEBIT_NOTE',
    department: 'PHARMACY',
    doc_no: dn.debit_note_no,
    date: dn.debit_note_date,
    party: dn.credit_note?.original_bill?.patient_name || '—',
    amount: dn.amount,
    status: dn.status,
    route: dn.credit_note?.original_bill?.id ? `/pharmacy/billing/${dn.credit_note.original_bill.id}` : null,
  }));
}

function normalizeOpdBills(rows) {
  return (rows || []).map(bill => ({
    key: `opd-bill-${bill.id}`,
    doc_type: 'BILL',
    department: 'OPD',
    doc_no: bill.bill_no,
    date: bill.bill_date,
    party: opdContactName(bill.contactInfo, bill.irm_contacts_id),
    amount: bill.total_amount,
    status: bill.status,
    route: `/opd/billing/${bill.id}`,
  }));
}

export default function AccountsDocumentsPage() {
  const navigate = useNavigate();
  const { readParams, writeParams } = useListUrlState();
  const urlParams = readParams();

  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(urlParams?.get('search') || '');
  const [department, setDepartment] = useState(urlParams?.get('department') || '');
  const [docType, setDocType] = useState(urlParams?.get('doc_type') || '');
  const [fromDate, setFromDate] = useState(urlParams?.get('from_date') || defaultFrom);
  const [toDate, setToDate] = useState(urlParams?.get('to_date') || defaultTo);
  const [page, setPage] = useState((urlParams && parseInt(urlParams.get('page'), 10)) || 1);

  const load = useCallback(async (fd, td) => {
    setLoading(true); setError('');
    try {
      const [bills, creditNotes, debitNotes, opdBills] = await Promise.all([
        api.pharmacy.listBills({ from_date: fd, to_date: td, per_page: FETCH_CAP, page: 1 }),
        api.pharmacy.listAllCreditNotes({ from_date: fd, to_date: td, per_page: FETCH_CAP, page: 1 }),
        api.pharmacy.listDebitNotes({ from_date: fd, to_date: td, per_page: FETCH_CAP, page: 1 }),
        api.opdBilling.listBills({ start_date: fd, end_date: td, limit: FETCH_CAP, offset: 0 }),
      ]);
      const merged = [
        ...normalizePharmacyBills(bills?.rows),
        ...normalizePharmacyCreditNotes(creditNotes?.rows),
        ...normalizePharmacyDebitNotes(debitNotes?.rows),
        ...normalizeOpdBills(opdBills?.data),
      ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setAllRows(merged);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load documents');
      setAllRows([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(fromDate, toDate); }, [load]); // eslint-disable-line

  // Search/department/docType/page are filtered client-side (no re-fetch),
  // so keep the URL in sync with every filter/page change here.
  useEffect(() => {
    writeParams({ from_date: fromDate, to_date: toDate, search, department, doc_type: docType, page });
  }, [fromDate, toDate, search, department, docType, page, writeParams]);

  const handleDateFilter = (fd, td) => { setFromDate(fd); setToDate(td); setPage(1); load(fd, td); };

  const filtered = allRows.filter(r => {
    if (department && r.department !== department) return false;
    if (docType && r.doc_type !== docType) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${r.party || ''} ${r.doc_no || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const total = filtered.length;
  const totalPages = Math.ceil(total / PER_PAGE);
  const pageRows = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const pageTotal = pageRows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const billCount = filtered.filter(r => r.doc_type === 'BILL').length;
  const noteCount = filtered.filter(r => r.doc_type !== 'BILL').length;

  const COL_HEADERS = ['Date', 'Dept', 'Type', 'Doc No', 'Party', 'Amount', 'Status'];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={0}>
        <Box sx={{
          borderRadius: 4, background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 60%, #003d4d 100%)`,
          px: { xs: 2.5, md: 4 }, py: 3, mb: 3, display: 'flex', alignItems: 'center', gap: 2,
          boxShadow: `0 8px 32px ${TEAL}44`, position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
          <Box sx={{ width: 46, height: 46, borderRadius: 2.5, background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
            <ReceiptLongIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box sx={{ zIndex: 1 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Accounts</Typography>
            <Typography sx={{ color: '#fff', fontSize: '1.35rem', fontWeight: 800, lineHeight: 1.1 }}>Documents</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.75rem', mt: 0.3 }}>Bills, credit &amp; debit notes — Pharmacy + OPD, in one place</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <StatCard icon={<ReceiptLongIcon />} label="Bills" value={billCount} accent={TEAL} sub="in current filter" />
          <StatCard icon={<DescriptionIcon />} label="Credit / Debit Notes" value={noteCount} accent="#8e24aa" sub="in current filter" />
          <StatCard icon={<DescriptionIcon />} label="Page Total" value={fmt(pageTotal)} accent="#2e7d32" sub={`${pageRows.length} of ${total} records`} />
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: 2.5, fontSize: '0.82rem' }}>{error}</Alert>}

        <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #f0f2f5', overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #f0f4f8', background: '#fafcff' }}>
            <TextField
              size="small" placeholder="Search party or document no…"
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#b0bec5', fontSize: 18 }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: '0.82rem', background: '#fff' } }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Department</InputLabel>
              <Select value={department} label="Department" onChange={e => { setDepartment(e.target.value); setPage(1); }} sx={DROPDOWN_SX}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PHARMACY">Pharmacy</MenuItem>
                <MenuItem value="OPD">OPD</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Document Type</InputLabel>
              <Select value={docType} label="Document Type" onChange={e => { setDocType(e.target.value); setPage(1); }} sx={DROPDOWN_SX}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="BILL">Bill</MenuItem>
                <MenuItem value="CREDIT_NOTE">Credit Note</MenuItem>
                <MenuItem value="DEBIT_NOTE">Debit Note</MenuItem>
              </Select>
            </FormControl>
            <MDDatePicker label="From" value={fromDate} onChange={val => handleDateFilter(val || '', toDate)} sx={{ width: 150 }} />
            <MDDatePicker label="To" value={toDate} onChange={val => handleDateFilter(fromDate, val || '')} sx={{ width: 150 }} />
            {(fromDate !== defaultFrom || toDate !== defaultTo) && (
              <Chip label="Reset" size="small" clickable onClick={() => handleDateFilter(defaultFrom, defaultTo)}
                sx={{ fontSize: '0.65rem', height: 26, color: '#ef5350', background: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: '20px' }} />
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
              <CircularProgress sx={{ color: TEAL }} size={36} />
              <Typography sx={{ fontSize: '0.8rem', color: '#b0bec5' }}>Loading documents…</Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableBody>
                  <TableRow sx={{ background: `linear-gradient(90deg, ${TEAL} 0%, ${TEAL_DARK} 100%)` }}>
                    {COL_HEADERS.map(h => (
                      <TableCell key={h} sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', textTransform: 'uppercase', letterSpacing: 0.7, py: 1.4, whiteSpace: 'nowrap', borderBottom: 'none', '&:first-of-type': { pl: 2.5 }, '&:last-of-type': { pr: 2.5 } }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>

                  {pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COL_HEADERS.length}>
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                          <ReceiptLongIcon sx={{ fontSize: 52, color: '#e0e0e0', mb: 1.5 }} />
                          <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#bdbdbd' }}>No documents found</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: '#d0d0d0', mt: 0.5 }}>Try adjusting your search, filters or date range</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : pageRows.map((r, i) => (
                    <TableRow
                      key={r.key}
                      hover
                      onClick={() => r.route && navigate(r.route)}
                      sx={{ background: i % 2 === 0 ? '#fff' : '#fafcff', cursor: r.route ? 'pointer' : 'default', '&:hover': { background: r.route ? TEAL_LIGHT : undefined } }}
                    >
                      <TableCell sx={{ py: 1.5, pl: 2.5 }}>
                        <Typography sx={{ fontSize: '0.76rem', color: '#546e7a', whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}><DeptBadge department={r.department} /></TableCell>
                      <TableCell sx={{ py: 1.5 }}><DocTypeBadge docType={r.doc_type} /></TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {r.route ? (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: '0.73rem', color: TEAL, fontFamily: 'monospace', fontWeight: 700 }}>
                            {r.doc_no || '—'}
                            <OpenInNewIcon sx={{ fontSize: '11px !important' }} />
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: '0.73rem', fontFamily: 'monospace', color: '#78909c' }}>{r.doc_no || '—'}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#2d3748' }}>{r.party}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography sx={{ fontSize: '0.85rem', fontFamily: 'monospace', fontWeight: 800, color: '#2d3748', whiteSpace: 'nowrap' }}>{fmt(r.amount)}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5, pr: 2.5 }}><StatusChip status={r.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 1.75, borderTop: '1px solid #f0f4f8', background: '#fafcff' }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#90a4ae', fontWeight: 500 }}>
                Showing <b style={{ color: '#546e7a' }}>{(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)}</b> of <b style={{ color: '#546e7a' }}>{total}</b> results
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  sx={{ minWidth: 0, border: '1.5px solid #e2e8f0', borderRadius: 2, color: page <= 1 ? '#d0d0d0' : TEAL, p: 0.5 }}>
                  <KeyboardArrowLeftIcon fontSize="small" />
                </Button>
                <Button size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                  sx={{ minWidth: 0, border: '1.5px solid #e2e8f0', borderRadius: 2, color: page >= totalPages ? '#d0d0d0' : TEAL, p: 0.5 }}>
                  <KeyboardArrowRightIcon fontSize="small" />
                </Button>
              </Box>
            </Box>
          )}
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
